import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import updateBillTosSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateBillTosSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { getBillTosParameter } = state.pages.rfqMyQuotes;
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        getWishListsParameter: state.pages.myLists.getWishListsParameter,
        billTosDataView: getBillTosParameter && getBillTosDataView(state, getBillTosParameter),
        getBillTosParameter,
    };
};

const mapDispatchToProps = {
    updateLoadParameter,
    loadBillTos,
    updateBillTosSearchFields,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListCustomerDropdownStyles {
    wrapper?: InjectableCss;
    customerDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const MyListCustomerDropdownStyles: MyListCustomerDropdownStyles = {
    wrapper: {
        css: css`
            margin-top: 15px;
        `,
    },
};

const styles = MyListCustomerDropdownStyles;

const MyListCustomerDropdown = ({
    billTosDataView,
    getBillTosParameter,
    loadBillTos,
    updateBillTosSearchFields,
    getWishListsParameter,
    updateLoadParameter,
}: Props) => {
    const [billToOptions, setBillToOptions] = React.useState<OptionObject[]>([]);
    const [billToSearch, setBillToSearch] = React.useState("");
    React.useEffect(() => {
        if (billTosDataView?.value) {
            setBillToOptions(getBillToOptions());
        } else if (billTosDataView && !billTosDataView.value && !billTosDataView.isLoading) {
            loadBillTos(getBillTosParameter);
        }
    }, [billTosDataView]);

    const EMTPY_ID = "EMTPY_ID";

    const changeCustomerSearchTextHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setBillToSearch(value);
        debouncedUpdateBillTosSearchFields(value);
    };

    const debouncedUpdateBillTosSearchFields = debounce((value: string) => {
        updateBillTosSearchFields({ ...getBillTosParameter, filter: value || undefined });
    }, 200);

    const selectCustomerHandler = (customerId?: string) => {
        updateLoadParameter({ customerId: customerId === EMTPY_ID || !customerId ? "" : customerId });
    };

    const getBillToOptions = () => {
        const options: OptionObject[] =
            billTosDataView?.value?.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            })) || [];
        options.unshift({ optionText: translate("Show All"), optionValue: EMTPY_ID });

        return options;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicDropdown
                data-test-selector="myList_customer"
                label={translate("Customer")}
                {...styles.customerDynamicDropdown}
                onSelectionChange={selectCustomerHandler}
                onInputChange={changeCustomerSearchTextHandler}
                selected={getWishListsParameter.customerId || EMTPY_ID}
                isLoading={billTosDataView?.isLoading}
                options={billToOptions}
                search={billToSearch}
                placeholder={translate("Search or Select Customer")}
                filterOption={() => true}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListCustomerDropdown),
    definition: {
        group: "My Lists",
        displayName: "My Lists Customer Dropdown",
        allowedContexts: ["MyListsPage"],
    },
};

export default widgetModule;
